import { Circle } from '@dealstack/frontend/shared/components/common/Circle';
import { DocumentCard } from '@dealstack/frontend/shared/components/DocumentCard/DocumentCard';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { InstructionsCard } from '@dealstack/frontend/investor/pages/Investment/common/Contracts/Instructions/InstructionsCard';
import { DataFileTct } from '@dealstack/shared/lib/generated';
import { formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { UPLOAD_BUTTON_VARIANT } from '@dealstack/frontend/investor/hooks/useWetInkModalFileHook';
import {
	Box,
	Button,
	Collapse,
	Divider,
	FileButton,
	Flex,
	Modal,
	Text,
	Title,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { uploadFileV2 } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';
import { cleanCopyFileId } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/SignKycFormModal/SignKycFormModal.utils';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { useAddFilledKycForm } from '@dealstack/frontend/investor/pages/Investment/hooks/useAddFilledKycForm';
import { FormPair } from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/types';
import { usePreFillKycForm } from '@dealstack/frontend/investor/pages/Investment/common/KycFormPreFill/usePreFillKycForm';

interface Props {
	className?: string;
	onClose: () => void;
	refreshData: () => void;
	form: FormPair;
	opened: boolean;
	kycWorkflowId: string;
}

export const SignKycFormModal = ({
	onClose,
	className,
	refreshData,
	form,
	opened,
	kycWorkflowId,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const { dealId } = useRequiredParams('dealId');

	const { downloadFile } = useDownloadFileUsingFileStorageUrl();
	const uploadFileRequest = useWrappedRequest(uploadFileV2);
	const addFilledKycForm = useAddFilledKycForm();
	const preFill = usePreFillKycForm(kycWorkflowId);

	const [uploadedForm, setUploadedForm] = useState<DataFileTct>();
	const [disableSaveButton, setDisableSaveButton] = useState(true);
	const [showInstructionCard, setShowInstructionCard] = useState(false);
	const [uploadButtonVariant, setUploadButtonVariant] = useState(UPLOAD_BUTTON_VARIANT.UPLOAD);
	const [isTemplateDownloaded, setIsTemplateDownloaded] = useState(false);
	const [disableDownloadTemplateButton, setDisableDownloadTemplateButton] = useState(false);

	const downloadTemplate = async () => {
		const preFilled = await preFill(form);
		await downloadFile({
			fileId: cleanCopyFileId(preFilled),
			dealId: dealId ?? '',
			fileName: form.name,
		});
	};

	useEffect(() => {
		setDisableSaveButton(!uploadedForm || uploadFileRequest.isLoading);
	}, [uploadFileRequest.isLoading, uploadedForm]);

	const onSaveButtonClicked = () => {
		if (!uploadedForm) {
			return;
		}
		return addFilledKycForm.mutate(
			{
				fileId: uploadedForm.id,
				templateId: form.templateId,
				kycWorkflowId,
			},
			{
				onSuccess: () => {
					refreshData();
					showToastV2({ title: tInvestor('investment.toast.form_uploaded') });
					onClose();
				},
			}
		);
	};

	const onFileDrop = (file: File) => {
		uploadFileRequest.fetch(file).onSuccess((result) => {
			const uploaded = result?.payload;
			if (uploaded?.id) {
				setUploadedForm(uploaded);
			}
			setUploadButtonVariant(UPLOAD_BUTTON_VARIANT.UPLOADED);
			showToastV2({ title: tInvestor('investment.toast.success') });
			setTimeout(() => {
				setUploadButtonVariant(UPLOAD_BUTTON_VARIANT.UPLOAD);
			}, 5000);
		});
	};

	const handleDownloadTemplate = async () => {
		setDisableDownloadTemplateButton(true);
		try {
			await downloadTemplate();
			setIsTemplateDownloaded(true);
		} finally {
			setDisableDownloadTemplateButton(false);
		}
	};

	const onDelete = () => {
		setUploadedForm(undefined);
		setUploadButtonVariant(UPLOAD_BUTTON_VARIANT.UPLOAD);
	};

	const isNewFormUploaded = !!uploadedForm?.id;

	return (
		<Modal
			title={
				<Title order={5}>
					{tInvestor('investment.join.kyc.kyc_forms.sign_kyc.complete_form')}
				</Title>
			}
			opened={opened}
			className={className}
			onClose={onClose}
			size="lg"
			centered
		>
			{addFilledKycForm.isPending || uploadFileRequest.isLoading ? <InlineLoader /> : null}
			{!!form.rejectionComment && !isNewFormUploaded && (
				<Text variant="p-lg" c="red.6" sx={{ marginBottom: '0.5rem' }}>
					{tInvestor('investment.join.kyc.proof_box.rejected')}
					{tInvestor('investment.join.kyc.proof_box.rejected_comment', {
						rejectionComment: form.rejectionComment,
					})}
				</Text>
			)}
			<Flex direction="column" gap="1rem">
				<Divider />

				<Flex align="baseline" gap="0.5rem">
					<Circle variant="filled">
						<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
							1
						</Text>
					</Circle>

					<Title order={5}>
						{tInvestor('investment.join.kyc.kyc_forms.sign_kyc.download')}
					</Title>
					<Box
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_FORM_DOWNLOAD_CLEAN_COPY}
					>
						<Button
							size="sm"
							variant={isTemplateDownloaded ? 'tertiary' : 'secondary'}
							sx={{ marginLeft: 'auto' }}
							leftSection={
								<Icon
									variant={isTemplateDownloaded ? 'tick' : 'download-v3'}
									size="lg"
								/>
							}
							disabled={disableDownloadTemplateButton}
							onClick={handleDownloadTemplate}
						>
							{tInvestor(
								`investment.join.kyc.kyc_forms.sign_kyc.${
									isTemplateDownloaded ? 'download_again' : 'download_clean'
								}`
							)}
						</Button>
					</Box>
				</Flex>
				<Divider />

				<Flex align="baseline" gap="0.5rem">
					<Circle variant="filled">
						<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
							2
						</Text>
					</Circle>
					<Flex direction="column" gap="0.5rem">
						<Title order={5}>
							{tInvestor('investment.join.kyc.kyc_forms.sign_kyc.fill')}
						</Title>
						<Text sx={{ maxWidth: '40rem' }}>
							{tInvestor(
								'investment.join.kyc.kyc_forms.sign_kyc.follow_instructions'
							)}
						</Text>
					</Flex>
				</Flex>
				<Divider />

				<Flex align="baseline" gap="0.5rem">
					<Circle variant="filled">
						<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
							3
						</Text>
					</Circle>
					<Flex direction="column" gap="0.5rem">
						<Title order={5}>
							{tInvestor('investment.join.kyc.kyc_forms.sign_kyc.upload')}
						</Title>
						<Text sx={{ maxWidth: '40rem' }}>
							{tInvestor('investment.join.kyc.kyc_forms.sign_kyc.upload_description')}
						</Text>
					</Flex>
					<FileButton onChange={onFileDrop} accept="application/pdf">
						{(props) =>
							uploadButtonVariant === UPLOAD_BUTTON_VARIANT.UPLOADED ? (
								<Button
									{...props}
									size="sm"
									variant="tertiary"
									sx={{ marginLeft: 'auto' }}
									leftSection={<Icon variant="tick" size="lg" />}
								>
									{tInvestor('investment.join.kyc.kyc_forms.sign_kyc.uploaded')}
								</Button>
							) : (
								<Box
									data-testid={
										INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_FORM_UPLOAD_BUTTON
									}
								>
									<Button
										{...props}
										size="sm"
										variant="secondary"
										sx={{ marginLeft: 'auto' }}
										leftSection={<Icon variant="upload-v2" size="lg" />}
									>
										{tInvestor(
											'investment.join.kyc.kyc_forms.sign_kyc.upload_button'
										)}
									</Button>
								</Box>
							)
						}
					</FileButton>
				</Flex>

				{isNewFormUploaded && (
					<DocumentCard
						documentTitle={uploadedForm.name}
						documentID={uploadedForm.id}
						date={formatStringDateToUI(uploadedForm.createdAt)}
						fullWidth={false}
					>
						<DocumentCard.DropdownMenu>
							<Box onClick={onDelete} sx={{ cursor: 'pointer' }}>
								<Text fz="sm" c="red.6">
									{tInvestor('investment.join.kyc.kyc_forms.sign_kyc.remove')}
								</Text>
							</Box>
						</DocumentCard.DropdownMenu>
					</DocumentCard>
				)}

				{!!form.instruction && (
					<Button
						variant="tertiary"
						onClick={() => {
							setShowInstructionCard(!showInstructionCard);
						}}
					>
						{tInvestor(
							`investment.join.kyc.kyc_forms.sign_kyc.${
								showInstructionCard ? 'close_instructions' : 'show_instructions'
							}`
						)}
					</Button>
				)}

				<Collapse in={showInstructionCard}>
					<InstructionsCard instruction={form.instruction} title={form.name} />
				</Collapse>
				<Divider />

				<Box data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_FORM_DONE_BUTTON}>
					<Button
						variant="primary"
						onClick={onSaveButtonClicked}
						disabled={disableSaveButton}
						sx={{ marginLeft: 'auto' }}
						leftSection={<Icon variant="tick" size="lg" />}
					>
						{tInvestor('investment.join.kyc.kyc_forms.sign_kyc.done')}
					</Button>
				</Box>
			</Flex>
		</Modal>
	);
};
